import moment from 'moment-timezone';
import React from 'react';

import {DataFrameColumnDef} from 'data-frames/DataFrameTypes';
import {convertToInternalFilterInterface} from 'data-frames/filters/components/convertFilterInterface';
import FilterEditorPopover, {
  FilterOptionsForPopover,
} from 'data-frames/filters/components/FilterEditorPopover';
import {FilterWithMetadata} from 'data-frames/filters/DataFrameFilterOptions';
import {
  DataFrameFilter,
  DataFrameFilterCondition,
  DataFrameFilterOperation,
} from 'data-frames/filters/DataFrameFilterTypes';
import {DataFrameReadOnlyInfo} from 'data-frames/header-menu/DataFrameReadOnlyInfo';
import columnToTypeIcon from 'data-frames/util/columnToTypeIcon';
import findColumn from 'data-frames/util/findColumn';
import isNumericCellValue from 'data-frames/util/isNumericCellValue';
import {LeafType} from 'data-types/benchling-type-def/BenchlingTypeDef';
import {ToggleDisplayMode} from 'ui-library/floating-ui/Float';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import * as DateUtil from 'util/DateUtil';
import {JSONValue} from 'util/Types';

import TransformPillBase from './TransformPillBase';

function valueToString(value: JSONValue, column: DataFrameColumnDef): string {
  if (Array.isArray(value)) {
    return value.map((v) => `"${v}"`).join(', ');
  }

  if (column.type.leafType === LeafType.DATETIME && typeof value === 'string') {
    const dateTimeValue = moment(value, moment.ISO_8601);
    if (dateTimeValue.isValid()) {
      return DateUtil.formalTime(dateTimeValue.toDate(), true);
    }
  }

  // TODO: replace api ids with names
  return isNumericCellValue(value, column) ? `${value}` : `"${String(value)}"`;
}

function conditionToString(filterCondition: DataFrameFilterCondition, column: DataFrameColumnDef): string {
  return filterCondition.operation === DataFrameFilterOperation.IS_NULL
    ? ''
    : ` ${valueToString(filterCondition.value, column)}`;
}

function filterToSummary(filter: DataFrameFilter, column: DataFrameColumnDef): string {
  const columnDisplayName = column.displayName;

  return `${columnDisplayName} ${filter.filters
    .map(
      (filterCondition) =>
        `${convertToInternalFilterInterface(filterCondition).operator}${conditionToString(
          filterCondition,
          column
        )}`
    )
    .join(filter.operation === DataFrameFilterOperation.AND ? ' and ' : ' or ')}`;
}

export interface FilterPillProps {
  disabled?: boolean;
  filterWithMetadata: FilterWithMetadata;
  filterOptions: FilterOptionsForPopover;
  readOnlyInfo: DataFrameReadOnlyInfo;
  onDelete: () => void;
}

export default function FilterPill({
  disabled = false,
  filterWithMetadata,
  filterOptions,
  readOnlyInfo,
  onDelete,
}: FilterPillProps): JSX.Element {
  const {filter, running} = filterWithMetadata;
  const filterSystemName = filter.filters[0].columnSystemName;
  const column = findColumn(filterOptions.dataFrameSpec, filterSystemName);
  const readOnly = readOnlyInfo.isReadOnly || readOnlyInfo.allUpdatesDisabledMessage != null;

  return (
    <FilterEditorPopover canSelectColumn={false} initialFilter={filter} filterOptions={filterOptions}>
      {(openMenu) => (
        <Tooltip
          toggleDisplayMode={ToggleDisplayMode.BLOCK_FIT_CONTENT}
          isDisabled={readOnlyInfo.allUpdatesDisabledMessage == null}
          text={<small>{readOnlyInfo.allUpdatesDisabledMessage}</small>}
        >
          <TransformPillBase
            text={column == null ? 'Invalid Filter' : filterToSummary(filter, column)}
            icon={column != null ? columnToTypeIcon(column) : undefined}
            disabled={disabled || readOnly}
            isRunning={running}
            onClick={openMenu}
            hasError={filterWithMetadata.failed || column == null}
            onDelete={onDelete}
          />
        </Tooltip>
      )}
    </FilterEditorPopover>
  );
}
